// src/pages/admin/properties/PropertiesAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../context/ApiContext";
import { Plus, Pencil, Trash2, MapPin, RefreshCcw } from "lucide-react";

const DEFAULT_LOCATION = "Johannesburg"; // works with your current backend filter

export default function PropertiesAdmin() {
  const navigate = useNavigate();
  const {
    adminListProperties: fetchProperties,
    adminDeleteProperty,
    adminListUnitsForMany: fetchUnitsForMany,
    adminListUnits: fetchUnits,
  } = useApi();

  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [rows, setRows] = useState([]);
  const [unitsCountMap, setUnitsCountMap] = useState({}); // slug -> number
  const [minPriceMap, setMinPriceMap] = useState({});     // slug -> number|null

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function load() {
    try {
      setLoading(true);
      setErr("");

      // 1) Get properties for current location
      const data = await fetchProperties({ location });
      const list = Array.isArray(data) ? data : [];
      setRows(list);

      // 2) Derive units aggregates for each property
      const slugs = list.map((p) => p.slug).filter(Boolean);
      if (slugs.length === 0) {
        setUnitsCountMap({});
        setMinPriceMap({});
        return;
      }

      // If backend already returns counts/minPrice on property, prefer that
      // (e.g., p.unitsCount, p.minUnitPrice). We'll still compute if missing.
      const prefilledCounts = {};
      const prefilledMin = {};
      list.forEach((p) => {
        if (typeof p.unitsCount === "number") prefilledCounts[p.slug] = p.unitsCount;
        if (typeof p.minUnitPrice === "number") prefilledMin[p.slug] = p.minUnitPrice;
      });
      setUnitsCountMap((prev) => ({ ...prefilledCounts })); // start with whatever we have
      setMinPriceMap((prev) => ({ ...prefilledMin }));

      // Need to compute missing ones:
      const needCompute = slugs.filter(
        (slug) => prefilledCounts[slug] == null || prefilledMin[slug] == null
      );
      if (needCompute.length === 0) return;

      // Prefer batch fetch if available
      if (typeof fetchUnitsForMany === "function") {
        try {
          const map = await fetchUnitsForMany(needCompute); // expected { [slug]: Unit[] }
          const nextCounts = { ...prefilledCounts };
          const nextMin = { ...prefilledMin };

          for (const slug of needCompute) {
            const units = Array.isArray(map?.[slug]) ? map[slug] : [];
            nextCounts[slug] = units.length;

            const available = units.filter((u) => u.available);
            const pool = (available.length ? available : units).map((u) => Number(u.price || 0));
            nextMin[slug] = pool.length ? Math.min(...pool) : null;
          }

          setUnitsCountMap(nextCounts);
          setMinPriceMap(nextMin);
        } catch {
          // Fall back to per-slug fetch
          if (typeof fetchUnits === "function") {
            const pairs = await Promise.all(
              needCompute.map(async (slug) => {
                try {
                  const units = await fetchUnits(slug);
                  const arr = Array.isArray(units) ? units : [];
                  const available = arr.filter((u) => u.available);
                  const pool = (available.length ? available : arr).map((u) => Number(u.price || 0));
                  const min = pool.length ? Math.min(...pool) : null;
                  return [slug, { count: arr.length, min }];
                } catch {
                  return [slug, { count: 0, min: null }];
                }
              })
            );
            const nextCounts = { ...prefilledCounts };
            const nextMin = { ...prefilledMin };
            for (const [slug, { count, min }] of pairs) {
              nextCounts[slug] = count;
              nextMin[slug] = min;
            }
            setUnitsCountMap(nextCounts);
            setMinPriceMap(nextMin);
          }
        }
      } else if (typeof fetchUnits === "function") {
        // No batch: per-slug fetch
        const pairs = await Promise.all(
          needCompute.map(async (slug) => {
            try {
              const units = await fetchUnits(slug);
              const arr = Array.isArray(units) ? units : [];
              const available = arr.filter((u) => u.available);
              const pool = (available.length ? available : arr).map((u) => Number(u.price || 0));
              const min = pool.length ? Math.min(...pool) : null;
              return [slug, { count: arr.length, min }];
            } catch {
              return [slug, { count: 0, min: null }];
            }
          })
        );
        const nextCounts = { ...prefilledCounts };
        const nextMin = { ...prefilledMin };
        for (const [slug, { count, min }] of pairs) {
          nextCounts[slug] = count;
          nextMin[slug] = min;
        }
        setUnitsCountMap(nextCounts);
        setMinPriceMap(nextMin);
      }
    } catch (e) {
      setErr(e?.message || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await load();
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesQ =
        !ql ||
        r.name?.toLowerCase().includes(ql) ||
        r.slug?.toLowerCase().includes(ql) ||
        r.address?.toLowerCase().includes(ql) ||
        r.agentName?.toLowerCase().includes(ql);
      const matchesStatus = status === "All" || r.status === status;
      return matchesQ && matchesStatus;
    });
  }, [rows, q, status]);

  async function handleDelete(slug) {
    if (!window.confirm("Delete this property?")) return;
    try {
      await adminDeleteProperty(slug);
      setRows((prev) => prev.filter((p) => p.slug !== slug));
      // Clean up aggregates
      setUnitsCountMap((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      setMinPriceMap((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
    } catch (e) {
      alert(e?.message || "Delete failed");
    }
  }

  function formatPrice(v) {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    }).format(n);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-2xl font-bold">Properties</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/admin/properties/new")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white"
          >
            <Plus className="w-4 h-4" />
            New Property
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="col-span-1 md:col-span-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, slug, address, or agent…"
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
          <div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
            >
              <option>All</option>
              <option>Rent</option>
              <option>Sale</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Location"
                className="w-full border rounded-md pl-8 pr-3 py-2"
              />
            </div>
            <button
              onClick={load}
              title="Refresh"
              className="px-3 py-2 border rounded-md bg-gray-50"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Slug</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Location</th>
                <th className="text-right px-4 py-2">Units</th>
                <th className="text-right px-4 py-2">Min Price</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-center" colSpan={8}>
                    Loading…
                  </td>
                </tr>
              ) : err ? (
                <tr>
                  <td className="px-4 py-6 text-red-600" colSpan={8}>
                    {err}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-gray-500" colSpan={8}>
                    No properties found.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const unitsCount =
                    typeof p.unitsCount === "number" ? p.unitsCount : unitsCountMap[p.slug];
                  const minPrice =
                    typeof p.minUnitPrice === "number" ? p.minUnitPrice : minPriceMap[p.slug];

                  return (
                    <tr key={p.slug} className="border-t">
                      <td className="px-4 py-2 font-medium">{p.name}</td>
                      <td className="px-4 py-2 text-gray-600">{p.slug}</td>
                      <td className="px-4 py-2">{p.category}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold
                          ${
                            p.status === "Rent"
                              ? "bg-green-100 text-green-800"
                              : p.status === "Sale"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">{p.location}</td>
                      <td className="px-4 py-2 text-right">{unitsCount ?? "—"}</td>
                      <td className="px-4 py-2 text-right">{minPrice == null ? "—" : formatPrice(minPrice)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/properties/${p.slug}`)}
                            className="px-2 py-1 rounded-md border"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/admin/properties/${p.slug}/edit`)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(p.slug)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
